import { useEffect, useRef, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Play, Pause, Volume2, VolumeX } from "lucide-react";
import { mediaUrl } from "@/lib/api";

export function Lightbox({ video, onClose, onPrev, onNext }) {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(false);
  const [progress, setProgress] = useState(0);

  const togglePlay = useCallback(() => {
    const el = videoRef.current;
    if (!el) return;
    if (el.paused) {
      el.play().catch(() => {});
    } else {
      el.pause();
    }
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
      if (e.key === " ") {
        e.preventDefault();
        togglePlay();
      }
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose, onPrev, onNext, togglePlay]);

  const onTimeUpdate = () => {
    const el = videoRef.current;
    if (el && el.duration) setProgress((el.currentTime / el.duration) * 100);
  };

  const seek = (e) => {
    const el = videoRef.current;
    if (!el || !el.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    el.currentTime = ratio * el.duration;
  };

  const isVertical = video.format === "vertical";

  return (
    <motion.div
      className="fixed inset-0 z-50 bg-ink/95 flex items-center justify-center"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      data-testid="lightbox-overlay"
      onClick={onClose}
    >
      <button
        data-testid="lightbox-close"
        onClick={onClose}
        aria-label="Close player"
        className="absolute top-5 right-5 z-10 w-11 h-11 flex items-center justify-center text-cream hover:text-cream/60 transition-colors"
      >
        <X className="w-6 h-6" />
      </button>
      <button
        data-testid="lightbox-prev"
        onClick={(e) => { e.stopPropagation(); onPrev(); }}
        aria-label="Previous video"
        className="absolute left-3 md:left-8 z-10 w-11 h-11 flex items-center justify-center text-cream hover:text-cream/60 transition-colors"
      >
        <ChevronLeft className="w-7 h-7" />
      </button>
      <button
        data-testid="lightbox-next"
        onClick={(e) => { e.stopPropagation(); onNext(); }}
        aria-label="Next video"
        className="absolute right-3 md:right-8 z-10 w-11 h-11 flex items-center justify-center text-cream hover:text-cream/60 transition-colors"
      >
        <ChevronRight className="w-7 h-7" />
      </button>

      <div
        className={`relative ${isVertical ? "h-[82vh] aspect-[9/16]" : "w-[86vw] max-w-4xl aspect-video"}`}
        onClick={(e) => e.stopPropagation()}
      >
        <video
          key={video.id}
          ref={videoRef}
          src={mediaUrl(video.video_url)}
          className="w-full h-full object-contain bg-ink"
          autoPlay
          loop
          playsInline
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onTimeUpdate={onTimeUpdate}
          data-testid="lightbox-video"
        />
        <div className="absolute bottom-0 inset-x-0 p-4 bg-gradient-to-t from-ink/80 to-transparent">
          <div
            className="h-1 bg-cream/25 cursor-pointer mb-3"
            onClick={seek}
            data-testid="lightbox-progress"
            role="slider"
            aria-label="Seek"
            aria-valuenow={Math.round(progress)}
          >
            <div className="h-full bg-cream" style={{ width: `${progress}%` }} />
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button data-testid="lightbox-play-toggle" onClick={togglePlay} aria-label={playing ? "Pause" : "Play"} className="w-10 h-10 flex items-center justify-center text-cream hover:text-cream/60 transition-colors">
                {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
              </button>
              <button
                data-testid="lightbox-mute-toggle"
                onClick={() => {
                  const el = videoRef.current;
                  if (el) el.muted = !el.muted;
                  setMuted((m) => !m);
                }}
                aria-label={muted ? "Unmute" : "Mute"}
                className="w-10 h-10 flex items-center justify-center text-cream hover:text-cream/60 transition-colors"
              >
                {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
              </button>
            </div>
            <p className="text-cream/80 text-xs uppercase tracking-[0.16em] pr-1">
              {video.title}{(video.categories?.length ? video.categories.join(" · ") : video.brand) ? ` — ${(video.categories?.length ? video.categories.join(" · ") : video.brand)}` : ""}
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
